import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL, Countries, RoleEnum } from "../../Constants";
import { v4 as uuidv4 } from "uuid";
import {
  CloseCircleOutlined,
  SaveOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { Alert, Button, Select } from "antd";
import OfficeFormModal from "./OfficeFormModal";
import DepartmentFormModal from "./DepartmentFormModal";
import AdminFormModal from "./AdminFormModal";
import TabNavigation from "./TabNavigation";
import SubscriptionManagementTab from "./SubscriptionSelector";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import EventListPage from "./events/EventListPage";
import CardProfileFormModal from "./CardProfileModal";
import CardCustomizer from "../shared/CardCustomizer";
import CardProfileTag from "../shared/CardProfileTag";

const CompanyForm = () => {
  const { Option } = Select;
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    email: "",
    website: "",
    phone: "",
    countryCode: "",
    country: "",
    currency: "SAR",
    isActive: true,
    subscriptionId: "1",
    isSubscribed: false,
    hasEventAccess: false,
    maxEventsCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [companyList, setCompanyList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [cardProfiles, setCardProfiles] = useState([]);
  const [cardProfileModalVisible, setCardProfileModalVisible] = useState(false);
  const [editingCardProfile, setEditingCardProfile] = useState(null);
  const [cardProfileLoading, setCardProfileLoading] = useState(false);
  const [customizerProfile, setCustomizerProfile] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [subCompanyUsage, setSubCompanyUsage] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { profile } = useContext(AppContext);

  // When a Parent admin creates a new company it becomes a Sub-Company and consumes
  // the Parent's MaxSubCompanies quota. Super admins create top-level parents (no limit).
  const atSubCompanyLimit =
    !id &&
    profile?.role !== "super_admin" &&
    subCompanyUsage &&
    subCompanyUsage.max != null &&
    subCompanyUsage.max > 0 &&
    subCompanyUsage.used >= subCompanyUsage.max;
  const validateRequiredFields = () => {
    const { name, country } = formValues;

    const isNameValid = name && name.trim() !== "";
    const isCountryValid = country && country.trim() !== "";
    // const isEmailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    return isNameValid && isCountryValid;
  };

  // const handleAdminSubmit = (values) => {
  //   const newAdmin = {
  //     ...values,
  //     id: editingItem ? editingItem.id : uuidv4(),
  //     role: "admin",
  //     isActive: true,
  //   };

  //   if (editingItem) {
  //     setAdmins(
  //       admins.map((admin) => (admin.id === editingItem.id ? newAdmin : admin))
  //     );
  //   } else {
  //     setAdmins([...admins, newAdmin]);
  //   }

  //   setIsModalVisible(false);
  //   setEditingItem(null);
  // };

  const handleAdminDelete = (id) => {
    setAdmins(admins.filter((admin) => admin.id !== id));
  };

  useEffect(() => {
    fetchCompanies();
    if (id) {
      fetchCompany(id);
    } else if (profile?.role !== "super_admin" && profile?.companyId) {
      // Warn before the admin fills the form if the Parent is out of sub-company slots.
      axios
        .get(
          `${BASE_URL}/Company/sub-company-usage?companyId=${profile.companyId}`
        )
        .then((res) => setSubCompanyUsage(res.data))
        .catch((err) =>
          console.error("Error fetching sub-company usage:", err)
        );
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === "subcompanies") {
      fetchSubCompanies();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (id && activeTab === "cardProfiles") {
      fetchCardProfiles();
    }
  }, [id, activeTab]);

  const fetchCompany = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/Company/${id}`);
      setFormValues({
        ...response.data,
        isActive: response.data.isActive || true,
      });
      setDepartments(response.data.departments || []);
      setOffices(response.data.offices || []);
      setAdmins(response.data.admins || []);
      if (response.data.iconUrl) {
        setImagePreview(response.data.iconUrl);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching company:", error);
      toast.error("Failed to fetch company details");
      setLoading(false);
    }
  };
  const handleCountryChange = (value) => {
    const selectedCountry = Countries.find(
      (country) => country.label === value
    );
    setFormValues((prev) => ({
      ...prev,
      country: selectedCountry.label,
      countryCode: selectedCountry.code,
    }));
  };
  const onFinish = async (event, departments, offices) => {
    event?.preventDefault();
    setLoading(true);
    setSaveError(null);
    const formData = new FormData();

    Object.keys(formValues).forEach((key) => {
      if (
        key !== "departments" &&
        key !== "offices" &&
        key !== "admins" &&
        key !== "subscriptionDetails" &&
        key !== "cardProfiles" &&
        key !== "digitalCardConfig"
      ) {
        formData.append(`CompanyItem.${key}`, formValues[key]);
      }
    });

    if (id) {
      formData.append("CompanyItem.Id", id);
    }
    if (file) {
      formData.append("File", file);
    }
    departments.forEach((dept, index) => {
      Object.keys(dept).forEach((key) => {
        formData.append(`CompanyItem.departments[${index}].${key}`, dept[key]);
      });
    });

    offices.forEach((office, index) => {
      Object.keys(office).forEach((key) => {
        formData.append(`CompanyItem.offices[${index}].${key}`, office[key]);
      });
    });
    // admins.forEach((admin, index) => {
    //   Object.keys(admin).forEach((key) => {
    //     formData.append(`CompanyItem.admins[${index}].${key}`, admin[key]);
    //   });
    // });
    try {
      const response = await axios({
        method: id ? "put" : "post",
        url: `${BASE_URL}/Company`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      // Get ID from response (adjust based on API response format)
      const newId = response.data?.id || id;
      toast.success(`Company ${id ? "updated" : "created"} successfully`);
      navigate(`/company-form/${newId}`);
    } catch (error) {
      console.error("Error saving company:", error);
      // Surface server-provided messages (e.g. sub-company limit reached,
      // one-level hierarchy violations) instead of a generic error.
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (typeof error.response?.data === "string"
          ? error.response.data
          : null);
      setSaveError(serverMessage || "Failed to save company");
      toast.error(serverMessage || "Failed to save company");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setImagePreview(URL.createObjectURL(file));
      toast.success(`${file.name} file uploaded successfully`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = () => {
    setFormValues((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  const showModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setIsModalVisible(true);
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/Lookup/parentCompanies`);
      setCompanyList(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubCompanies = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/Company/GetSubCompanies?companyId=${id}`
      );
      setSubCompanies(response.data);
    } catch (error) {
      console.error("Error fetching sub companies:", error);
      toast.error("Failed to fetch sub companies");
    } finally {
      setLoading(false);
    }
  };

  const fetchCardProfiles = async () => {
    if (!id) return;
    setCardProfileLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/Company/${id}/card-profiles`
      );
      setCardProfiles(response.data || []);
    } catch (error) {
      console.error("Error fetching card profiles:", error);
      toast.error("Failed to fetch card profiles");
    } finally {
      setCardProfileLoading(false);
    }
  };

  const handleCardProfileSubmit = async (formData) => {
    const payload = new FormData();
    payload.append("Name", formData.name);

    try {
      if (editingCardProfile) {
        await axios.put(
          `${BASE_URL}/Company/${id}/card-profiles/${editingCardProfile.id}`,
          payload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Card profile updated successfully");
      } else {
        await axios.post(
          `${BASE_URL}/Company/${id}/card-profiles`,
          payload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Card profile created successfully");
      }
      setCardProfileModalVisible(false);
      setEditingCardProfile(null);
      fetchCardProfiles();
    } catch (error) {
      console.error("Error saving card profile:", error);
      toast.error("Failed to save card profile");
    }
  };

  const handleCardProfileDelete = async (profileId) => {
    try {
      await axios.delete(
        `${BASE_URL}/Company/${id}/card-profiles/${profileId}`
      );
      toast.success("Card profile deleted");
      fetchCardProfiles();
    } catch (error) {
      console.error("Error deleting card profile:", error);
      toast.error("Failed to delete card profile");
    }
  };

  const handleSetDefaultProfile = async (profileId) => {
    try {
      await axios.put(
        `${BASE_URL}/Company/${id}/card-profiles/${profileId}/set-default`
      );
      toast.success("Default card profile updated successfully");
      fetchCardProfiles();
    } catch (error) {
      console.error("Error setting default card profile:", error);
      toast.error("Failed to set default card profile");
    }
  };

  const handleCardProfileCancel = () => {
    setCardProfileModalVisible(false);
    setEditingCardProfile(null);
  };

  const handleCardConfigSubmit = async (cardData) => {
    if (!customizerProfile) return;

    const payload = new FormData();
    payload.append("Name", customizerProfile.name);

    Object.keys(cardData).forEach((key) => {
      if (key === "textStyles") {
        Object.entries(cardData.textStyles).forEach(([styleKey, styleValue]) => {
          Object.entries(styleValue).forEach(([property, value]) => {
            payload.append(
              `Config.TextStyles[${styleKey}].${property}`,
              value.toString()
            );
          });
        });
      } else if (key !== "backgroundImage" && key !== "backgroundImagePreview") {
        payload.append(`Config.${key}`, cardData[key]);
      }
    });

    if (cardData.useBackgroundImage && cardData.backgroundImage) {
      payload.append("BackgroundImage", cardData.backgroundImage);
    }

    try {
      await axios.put(
        `${BASE_URL}/Company/${id}/card-profiles/${customizerProfile.id}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Card profile design saved successfully");
      setCustomizerProfile(null);
      fetchCardProfiles();
    } catch (error) {
      console.error("Error saving card profile design:", error);
      toast.error("Failed to save card profile design");
    }
  };

  const handleCompanyChange = (value, option) => {
    setFormValues((prev) => ({
      ...prev,
      parentCompanyId: option.value,
      parentCompanyName: option.children,
    }));
  };
  const handleModalOk = (values) => {
    let updatedOffices = offices;
    let updatedDepartments = departments;
    if (modalType === "department") {
      if (editingItem) {
        updatedDepartments = departments.map((dept) =>
          dept.id === editingItem.id ? { ...dept, ...values } : dept
        );
      } else {
        updatedDepartments = [...departments, { ...values, id: uuidv4() }];
      }
      setDepartments(updatedDepartments);
    } else if (modalType === "office") {
      if (editingItem) {
        updatedOffices = offices.map((office) =>
          office.id === editingItem.id ? { ...office, ...values } : office
        );
      } else {
        updatedOffices = [...offices, { ...values, id: uuidv4() }];
      }
      setOffices(updatedOffices);
    }
    setIsModalVisible(false);
    setEditingItem(null);
    onFinish(null, updatedDepartments, updatedOffices);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
  };

  const handleDelete = (type, id) => {
    if (type === "department") {
      setDepartments(departments.filter((dept) => dept.id !== id));
    } else if (type === "office") {
      setOffices(offices.filter((office) => office.id !== id));
    }
  };

  // const isFormValid = Object.values(formValues).every((value) => value) || id;
  const activateSubscription = async () => {
    try {
      await axios.post(
        `${BASE_URL}/Subscription/activate?companyId=${formValues.id}&subscriptionId=${formValues.subscriptionId}`
      );
      setFormValues((prev) => ({
        ...prev,
        isSubscribed: true,
      }));
      toast.success("Subscription activated successfully");
    } catch (error) {
      console.error("Error activating subscription:", error);
      toast.error("Failed to activate subscription");
    }
  };

  const deactivateSubscription = async () => {
    try {
      await axios.post(
        `${BASE_URL}/Subscription/deactivate?companyId=${formValues.id}&subscriptionId=${formValues.subscriptionId}`
      );
      setFormValues((prev) => ({
        ...prev,
        isSubscribed: false,
      }));

      toast.success("Subscription deactivated successfully");
    } catch (error) {
      console.error("Error deactivating subscription:", error);
      toast.error("Failed to deactivate subscription");
    }
  };

  const toggleSubscription = () => {
    if (formValues.isSubscribed) {
      deactivateSubscription();
    } else {
      activateSubscription();
    }
  };
  return (
    <div className="min-h-full bg-transparent p-6">
      <header className="py-4">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            {id ? t("pageTitle.editCompany") : t("pageTitle.newCompany")}
          </h1>
        </div>
      </header>
      <div className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        {/* Tabs */}
        <TabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          id={id}
          parentCompanyId={formValues.parentCompanyId}
        />

        {atSubCompanyLimit && (
          <Alert
            type="warning"
            showIcon
            className="my-3"
            message={t("company.subLimitReached", "Sub-company limit reached")}
            description={`Your organization has reached its sub-company limit (${subCompanyUsage.used}/${subCompanyUsage.max}). Contact your administrator to increase the limit before creating another sub-company.`}
          />
        )}

        {saveError && (
          <Alert
            type="error"
            showIcon
            className="my-3"
            message={t("company.saveFailed", "Unable to save company")}
            description={saveError}
            closable
            onClose={() => setSaveError(null)}
          />
        )}

        {activeTab === "details" && (
          <form onSubmit={(e) => onFinish(e, departments, offices)}>
            <div className="grid grid-cols-2 gap-x-2">
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.name")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formValues.name}
                  onChange={handleChange}
                  placeholder={t("placeholders.company.name")}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              {profile?.role === RoleEnum.SUPER_ADMIN && (
                <div className="">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("form.parentCompany")}
                  </label>
                  <Select
                    showSearch
                    value={
                      formValues.parentCompanyName === "null"
                        ? null
                        : formValues.parentCompanyName
                    }
                    placeholder={t("placeholders.company.parentCompany")}
                    onChange={handleCompanyChange}
                    className="mt-2 block w-full"
                    optionFilterProp="children"
                  >
                    {companyList
                      .filter((company) => company.id !== id)
                      .map((company) => (
                        <Select.Option key={company.id} value={company.id}>
                          {company.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )}
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.arabicName")}
                </label>
                <input
                  type="text"
                  name="arabicName"
                  value={
                    formValues.arabicName === "null"
                      ? undefined
                      : formValues.arabicName
                  }
                  onChange={handleChange}
                  placeholder={t("placeholders.company.arabicName")}
                  className="w-full px-3 py-2 border text-right rounded-lg"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t("form.description")}
              </label>
              <textarea
                name="description"
                value={formValues.description}
                onChange={handleChange}
                placeholder={t("placeholders.description")}
                className="w-full px-3 py-2 border rounded"
              />
            </div>

            {/* Email, Website, Phone, Country section */}
            <div className="grid grid-cols-2 gap-x-2">
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.email")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  placeholder={t("placeholders.email")}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.website")}
                </label>
                <input
                  type="url"
                  name="website"
                  value={formValues.website}
                  onChange={handleChange}
                  placeholder={t("placeholders.website")}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.phone")}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleChange}
                  placeholder={t("placeholders.phone")}
                  className="w-full px-3 py-2 border"
                  pattern="^[0-9]*$"
                />
              </div>

              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  {t("form.country")} <span className="text-red-500">*</span>
                </label>
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  placeholder={t("placeholders.country")}
                  optionFilterProp="children"
                  onChange={handleCountryChange}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  value={formValues.country || ""}
                >
                  {Countries.map((country) => (
                    <Option key={country.code} value={country.label}>
                      {country.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Logo upload section */}
              <div className="mb-3">
                <label className="block text-gray-600 font-medium mb-2">
                  {t("form.logo")}
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={handleUploadChange}
                    className="hidden"
                    id="fileUpload"
                  />
                  <label
                    htmlFor="fileUpload"
                    className="bg-indigo-500 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-indigo-600"
                  >
                    {t("buttons.chooseFile")}
                  </label>
                  {imagePreview && (
                    <div className="ml-4 flex items-center">
                      <img
                        src={imagePreview}
                        alt="Company Logo"
                        className="w-12 h-12 object-cover rounded-lg border ml-3"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setFile(null);
                        }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        {t("buttons.remove")}
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {t("fileUpload.supportedFormats")}{" "}
                  <span className="font-medium">
                    {t("fileUpload.formatTypes")}
                  </span>
                </p>
              </div>

              {/* Active toggle */}
              <div className="mb-4 flex items-center">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="checkbox"
                    checked={formValues.isActive}
                    onChange={handleToggleChange}
                    className="checkbox"
                  />
                  <label className="switch" htmlFor="checkbox">
                    <span className="slider"></span>
                  </label>
                </div>
                <label className="mr-2">{t("form.active")}</label>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-3 py-1 bg-red-600 text-white rounded-lg disabled:opacity-50"
                disabled={loading}
              >
                <CloseCircleOutlined />
                <span>{t("buttons.cancel")}</span>
              </button>
              <button
                type="submit"
                className={`flex items-center gap-2 px-3 py-1 ${
                  validateRequiredFields() ? "bg-indigo-600" : "bg-indigo-300"
                } text-white rounded-lg disabled:opacity-50`}
                disabled={!validateRequiredFields() || loading || atSubCompanyLimit}
              >
                {loading ? (
                  <span className="animate-pulse">{t("buttons.saving")}</span>
                ) : (
                  <>
                    <SaveOutlined />
                    <span>{t("buttons.save")}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Departments Tab */}
        {activeTab === "departments" && (
          <div>
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-bold">{t("tabs.departments")}</h2>
              <button
                type="button"
                onClick={() => showModal("department")}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1 rounded-lg flex items-center"
              >
                <PlusOutlined className="mr-2" />
                {t("buttons.new")}{" "}
                <span className="lg:inline-block hidden ml-1">
                  {t("tabs.departments")}
                </span>
              </button>
            </div>

            <div className="overflow-x-auto bg-white shadow-md rounded-lg">
              <table className="min-w-full leading-normal">
                <thead>
                  <tr>
                    {Object.keys(
                      t("table.department", { returnObjects: true })
                    ).map((key) => (
                      <th
                        key={key}
                        className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {t(`table.department.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept) => (
                    <tr key={dept.id}>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {dept.name}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {dept.description}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {dept?.arabicName}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => showModal("department", dept)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete("department", dept.id)}
                          danger
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === "offices" && (
          <div>
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-bold">{t("tabs.offices")}</h2>
              <button
                type="button"
                onClick={() => showModal("office")}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1 h-fit rounded-lg flex items-center"
              >
                <PlusOutlined className="mr-2" />
                {t("buttons.new")}{" "}
                <span className="lg:inline-block hidden ml-1">
                  {t("tabs.offices")}
                </span>
              </button>
            </div>
            <div className="overflow-x-auto bg-white shadow-md rounded-lg">
              <table className="min-w-full leading-normal">
                <thead>
                  <tr>
                    {Object.keys(
                      t("table.office", { returnObjects: true })
                    ).map((key) => (
                      <th
                        key={key}
                        className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {t(`table.office.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {offices.map((office) => (
                    <tr key={office.id}>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {office.name}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 max-w-56 truncate whitespace-no-wrap">
                          {office.address}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {office.city}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => showModal("office", office)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleDelete("office", office.id)}
                          danger
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Card Profiles Tab */}
        {activeTab === "cardProfiles" && id && (
          <div>
            {customizerProfile ? (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">
                    {customizerProfile.isDefault || customizerProfile.sourceProfileId
                      ? `View Profile: ${customizerProfile.name}`
                      : `Customize: ${customizerProfile.name}`}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setCustomizerProfile(null)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1 rounded-lg"
                  >
                    ← Back to Profiles
                  </button>
                </div>
                {(customizerProfile.isDefault || customizerProfile.sourceProfileId) && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                      <div className="text-sm text-yellow-700">
                        {customizerProfile.sourceProfileId
                          ? "This profile is inherited from the parent company and is read-only here."
                          : "To edit this card go to company settings"}
                      </div>
                    </div>
                  </div>
                )}
                <CardCustomizer
                  key={customizerProfile.id}
                  isPublicEmployee={false}
                  cardConfig={customizerProfile.digitalCardConfig}
                  companyLogoUrl={formValues.iconUrl}
                  handleCardSubmit={handleCardConfigSubmit}
                  isReadOnly={
                    customizerProfile.isDefault || !!customizerProfile.sourceProfileId
                  }
                />
              </div>
            ) : (
              <div>
                <div className="flex justify-between mb-4">
                  <h2 className="text-xl font-bold">Card Profiles</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCardProfile(null);
                      setCardProfileModalVisible(true);
                    }}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1 rounded-lg flex items-center"
                  >
                    <PlusOutlined className="mr-2" />
                    New{" "}
                    <span className="lg:inline-block hidden ml-1">
                      Card Profile
                    </span>
                  </button>
                </div>

                <div className="overflow-x-auto bg-white shadow-md rounded-lg">
                  <table className="min-w-full leading-normal">
                    <thead>
                      <tr>
                        <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Profile Name
                        </th>
                        <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Default
                        </th>
                        <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cardProfileLoading ? (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center text-gray-500"
                          >
                            Loading...
                          </td>
                        </tr>
                      ) : cardProfiles.length === 0 ? (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center text-gray-500"
                          >
                            No card profiles found. Create one to get started.
                          </td>
                        </tr>
                      ) : (
                        cardProfiles.map((profile) => {
                          const isInherited = !!profile.sourceProfileId;
                          const isReadOnly = profile.isDefault || isInherited;
                          return (
                          <tr key={profile.id}>
                            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                              <p className="text-gray-900 whitespace-no-wrap">
                                {profile.name}
                                <CardProfileTag
                                  profile={profile}
                                  tags={["inherited"]}
                                />
                              </p>
                            </td>
                            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                              <p className="text-gray-900 whitespace-no-wrap">
                                {profile.isDefault ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Default
                                  </span>
                                ) : (
                                  <span className="text-gray-500">No</span>
                                )}
                              </p>
                            </td>
                            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                              {isReadOnly ? (
                                <Button
                                  onClick={() => setCustomizerProfile(profile)}
                                  className="text-indigo-600 hover:text-indigo-900 mr-3"
                                >
                                  View
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setEditingCardProfile(profile);
                                      setCardProfileModalVisible(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                    title="Edit Name"
                                  />
                                  <Button
                                    onClick={() => setCustomizerProfile(profile)}
                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                  >
                                    Design
                                  </Button>
                                  <Button
                                    onClick={() =>
                                      handleSetDefaultProfile(profile.id)
                                    }
                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                  >
                                    Set Default
                                  </Button>
                                  <Button
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      handleCardProfileDelete(profile.id)
                                    }
                                    danger
                                  />
                                </>
                              )}
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === "admins" && (
          <div>
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-bold">{t("tabs.admins")}</h2>
              <button
                type="button"
                onClick={() => showModal("admin")}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-1 rounded-lg flex items-center"
              >
                <PlusOutlined className="mr-2" />
                {t("buttons.new")}{" "}
                <span className="lg:inline-block hidden ml-1">
                  {t("tabs.admins")}
                </span>
              </button>
            </div>

            <div className="overflow-x-auto bg-white shadow-md rounded-lg">
              <table className="min-w-full leading-normal">
                <thead>
                  <tr>
                    {Object.keys(t("table.admin", { returnObjects: true })).map(
                      (key) => (
                        <th
                          key={key}
                          className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                        >
                          {t(`table.admin.${key}`)}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id}>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {`${admin.firstName} ${admin.lastName}`}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {admin.email}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">
                          {admin.username}
                        </p>
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => showModal("admin", admin)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        />
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleAdminDelete(admin.id)}
                          danger
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === "subscription" && (
          <SubscriptionManagementTab
            formValues={formValues}
            onActivateTrial={async () => {
              try {
                await axios.post(
                  `${BASE_URL}/Subscription/activate-trial?companyId=${formValues.id}&subscriptionId=${formValues.subscriptionId}`
                );
                await fetchCompany(formValues.id);
                toast.success(t("messages.success.trialActivated"));
              } catch (error) {
                console.error("Error activating trial:", error);
                toast.error(t("messages.error.trialActivation"));
              }
            }}
            onActivateSubscription={async (durationType) => {
              try {
                await axios.post(
                  `${BASE_URL}/Subscription/activate?companyId=${formValues.id}&subscriptionId=${formValues.subscriptionId}&durationType=${durationType}`
                );
                await fetchCompany(formValues.id);
                toast.success(t("messages.success.subscriptionActivated"));
              } catch (error) {
                console.error("Error activating subscription:", error);
                toast.error(t("messages.error.subscriptionActivation"));
              }
            }}
            onDeactivateSubscription={async () => {
              try {
                await axios.post(
                  `${BASE_URL}/Subscription/deactivate?companyId=${formValues.id}&subscriptionId=${formValues.subscriptionId}`
                );
                await fetchCompany(formValues.id);
                toast.success(t("messages.success.subscriptionDeactivated"));
              } catch (error) {
                console.error("Error deactivating subscription:", error);
                toast.error(t("messages.error.subscriptionDeactivation"));
              }
            }}
          />
        )}

        {/* Sub Companies Tab */}
        {activeTab === "events" && (
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Event Module Access</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  id="hasEventAccess"
                  name="hasEventAccess"
                  type="checkbox"
                  checked={formValues.hasEventAccess || false}
                  onChange={(e) => {
                    setFormValues((prev) => ({
                      ...prev,
                      hasEventAccess: e.target.checked,
                    }));
                  }}
                  disabled={profile?.role !== RoleEnum.SUPER_ADMIN}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <label htmlFor="hasEventAccess" className="ml-2 block text-sm text-gray-900 font-medium cursor-pointer">
                  Enable Event Access for this Company
                </label>
              </div>

              {formValues.hasEventAccess && (
                <div className="max-w-xs">
                  <label htmlFor="maxEventsCount" className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Number of Events
                  </label>
                  <input
                    type="number"
                    id="maxEventsCount"
                    name="maxEventsCount"
                    value={formValues.maxEventsCount || 0}
                    onChange={(e) => {
                      setFormValues((prev) => ({
                        ...prev,
                        maxEventsCount: parseInt(e.target.value, 10) || 0,
                      }));
                    }}
                    disabled={profile?.role !== RoleEnum.SUPER_ADMIN}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The maximum number of events this company is allowed to create.
                  </p>
                </div>
              )}

              {profile?.role === RoleEnum.SUPER_ADMIN && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={(e) => onFinish(e, departments, offices)}
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === "subcompanies" &&
          profile?.role === RoleEnum.SUPER_ADMIN && (
            <div>
              <div className="flex justify-between mb-4">
                <h2 className="text-xl font-bold">{t("tabs.subCompanies")}</h2>
              </div>

              <div className="overflow-x-auto bg-white shadow-md rounded-lg">
                <table className="min-w-full leading-normal">
                  <thead>
                    <tr>
                      <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {t("form.name")}
                      </th>
                      <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {t("form.description")}
                      </th>
                      <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {t("form.email")}
                      </th>
                      <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {t("form.country")}
                      </th>
                      <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {t("form.active")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subCompanies.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-center text-gray-500"
                        >
                          {loading
                            ? t("common.loading")
                            : t("messages.noSubCompanies")}
                        </td>
                      </tr>
                    ) : (
                      subCompanies.map((subCompany) => (
                        <tr key={subCompany.id}>
                          <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                            <div className="flex items-center">
                              {subCompany.iconUrl && (
                                <img
                                  className="w-8 h-8 rounded-full mr-3"
                                  src={subCompany.iconUrl}
                                  alt={subCompany.name}
                                />
                              )}
                              <p className="text-gray-900 whitespace-no-wrap font-medium">
                                {subCompany.name}
                              </p>
                            </div>
                          </td>
                          <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                            <p className="text-gray-900 max-w-56 truncate whitespace-no-wrap">
                              {subCompany.description}
                            </p>
                          </td>
                          <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                            <p className="text-gray-900 whitespace-no-wrap">
                              {subCompany.email}
                            </p>
                          </td>
                          <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                            <p className="text-gray-900 whitespace-no-wrap">
                              {subCompany.country}
                            </p>
                          </td>
                          <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                            <span
                              className={`relative inline-block px-3 py-1 font-semibold leading-tight ${
                                subCompany.isActive
                                  ? "text-green-900"
                                  : "text-red-900"
                              }`}
                            >
                              <span
                                aria-hidden
                                className={`absolute inset-0 opacity-50 rounded-full ${
                                  subCompany.isActive
                                    ? "bg-green-200"
                                    : "bg-red-200"
                                }`}
                              ></span>
                              <span className="relative">
                                {subCompany.isActive
                                  ? t("form.active")
                                  : t("form.inactive")}
                              </span>
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
      {/* Modal for Department Form */}
      {isModalVisible && modalType === "department" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-lg">
            <DepartmentFormModal
              initialValues={editingItem}
              onSubmit={(values) => {
                const departmentData = {
                  ...values,
                  id: editingItem ? editingItem.id : Date.now(),
                };
                handleModalOk(departmentData);
              }}
              onCancel={handleModalCancel}
            />
          </div>
        </div>
      )}

      {/* Modal for Office Form */}
      {isModalVisible && modalType === "office" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-2xl">
            <OfficeFormModal
              initialValues={editingItem}
              onSubmit={(values) => {
                const officeData = {
                  ...values,
                  id: editingItem ? editingItem.id : Date.now(),
                };
                handleModalOk(officeData);
              }}
              onCancel={handleModalCancel}
            />
          </div>
        </div>
      )}

      {/* Modal for Card Profile Form */}
      {cardProfileModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-lg">
            <CardProfileFormModal
              initialValues={editingCardProfile}
              onSubmit={handleCardProfileSubmit}
              onCancel={handleCardProfileCancel}
            />
          </div>
        </div>
      )}

      {/* Modal for Admin Form */}
      {isModalVisible && modalType === "admin" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative w-full max-w-lg">
            <AdminFormModal
              initialValues={editingItem}
              onCancel={handleModalCancel}
              companyId={formValues.id}
              companyName={formValues.name}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyForm;
